'use client';
/* ── RÉGLAGES DES ALIQUOTS DE CONTRÔLE (paramétrage, 03/10/2026) ─────────────
 *
 * Rangés dans `materiauValidites` avec le délai de prévenance, et enregistrés
 * par le même bouton du paramétrage : un seul chemin d'écriture, validé et
 * journalisé par le moteur (`ged-setting.update` → `normaliserReglageProduit`).
 *
 * ⚠️ LA FICHE PRODUIT A QUITTÉ CET ÉCRAN (RQ, 03/10/2026). Les stabilités
 * (congelé, après décongélation), l'usage d'un aliquot et les volumes se
 * saisissent désormais avec la référence du FLACON, au geste « Reconstituer un
 * flacon », notice en main — dans l'esprit de la décision du 27/08 : « la
 * validité des matériaux doit être dans le référencement du produit et non dans
 * le paramétrage ». Le moteur REFUSE ces clés ici.
 *
 * Ne reste que ce qui n'est PAS une propriété du produit : le rythme de passage
 * du contrôle, les niveaux suivis, l'avance d'alerte de rupture (choix du
 * laboratoire), et l'étiquetage (taille, résolution, adresse de la Zebra,
 * grille A4).
 *
 * La liste des produits vient des données réelles (`GET ged/aliquots`) avec
 * leur clé : elle n'est pas reconstruite ici. */
import React, { useEffect, useState } from 'react';
import { inputStyle } from '@/components/ui';
import type { GedSettings } from '@/lib/types';
import type { ReglageProduitControle, ConfigEtiquettesConf, GrillePlancheConf } from '@/components/aliquots-types';

type Conf = NonNullable<GedSettings['materiauValidites']>;

const VIDE: ReglageProduitControle = { libelle: '', rythme: null, niveaux: [], alerteRuptureJours: null };

const petit: React.CSSProperties = { ...inputStyle, height: 30, fontSize: 12.5, width: 74, textAlign: 'end' };
const lbl: React.CSSProperties = { display: 'block', fontSize: 11.5, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 3 };

export function ReglagesAliquots({ value, onChange }: { value: Conf; onChange: (v: Conf) => void }) {
  const [produits, setProduits] = useState<{ cle: string; libelle: string }[] | null>(null);
  useEffect(() => {
    let vivant = true;
    void fetch('/api/ged/ged/aliquots', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { produits: [] }))
      .then((d) => { if (vivant) setProduits((d.produits || []).map((p: { cle: string; libelle: string }) => ({ cle: p.cle, libelle: p.libelle }))); })
      .catch(() => { if (vivant) setProduits([]); });
    return () => { vivant = false; };
  }, []);

  const reglages = value.produits || {};
  const poser = (cle: string, libelle: string, patch: Partial<ReglageProduitControle>) => {
    // Seules les clés du réglage sont recopiées : une entrée héritée de
    // l'ancienne fiche (stabilités…) serait refusée en bloc par le moteur.
    const avant = { ...VIDE, ...(reglages[cle] || {}) };
    const propre: ReglageProduitControle = {
      libelle, rythme: avant.rythme, niveaux: avant.niveaux, alerteRuptureJours: avant.alerteRuptureJours, ...patch,
    };
    onChange({ ...value, produits: { ...reglages, [cle]: propre } });
  };
  const etq: Partial<ConfigEtiquettesConf> = value.etiquettes || {};
  const poserEtq = (patch: Partial<ConfigEtiquettesConf>) => onChange({ ...value, etiquettes: { ...etq, ...patch } });
  const poserPlanche = (patch: Partial<GrillePlancheConf>) => poserEtq({ planche: { ...(etq.planche || {}), ...patch } as GrillePlancheConf });
  const num = (v: string) => { const n = Number(v.replace(',', '.')); return v.trim() === '' || !Number.isFinite(n) ? null : n; };

  return (
    <div style={{ display: 'grid', gap: 16, borderTop: '1px solid var(--border-subtle)', paddingTop: 14 }}>
      <div>
        <strong style={{ fontSize: 13.5, color: 'var(--text-strong)' }}>Aliquots de contrôle — rythme de passage</strong>
        <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
          Ce qui sert à <em>prévoir</em> les ruptures : à quel rythme chaque contrôle passe, quels
          niveaux il suit, et combien de jours à l’avance prévenir. Les <strong>stabilités</strong> (congelé,
          après décongélation), l’usage d’un aliquot et les volumes ne se règlent plus ici : ils se
          saisissent avec la référence du flacon, au geste <em>Reconstituer un flacon</em>, notice en main.
        </p>
      </div>
      {produits === null && <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Chargement des produits…</span>}
      {produits !== null && produits.length === 0 && (
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>Aucun produit de contrôle trouvé dans le registre des flacons.</span>
      )}
      {(produits || []).map((p) => {
        const f = { ...VIDE, ...(reglages[p.cle] || {}) };
        return (
          <fieldset key={p.cle} style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', display: 'grid', gap: 8 }}>
            <legend style={{ fontSize: 12.5, fontWeight: 700, padding: '0 4px' }}>{p.libelle}</legend>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label style={lbl} htmlFor={`${p.cle}-rythme`}>Rythme de passage</label>
                <select id={`${p.cle}-rythme`} value={f.rythme || ''} style={{ ...inputStyle, height: 30, fontSize: 12.5 }}
                  onChange={(e) => poser(p.cle, p.libelle, { rythme: (e.target.value || null) as ReglageProduitControle['rythme'] })}>
                  <option value="">— non renseigné —</option>
                  <option value="journalier">journalier</option>
                  <option value="lundi-jeudi">lundi et jeudi</option>
                  <option value="hebdomadaire">hebdomadaire</option>
                  <option value="quinzaine">quinzaine</option>
                  <option value="mensuel">mensuel</option>
                </select>
              </div>
              <div>
                <label style={lbl} htmlFor={`${p.cle}-niveaux`}>Niveaux attendus</label>
                <input id={`${p.cle}-niveaux`} style={{ ...petit, width: 90, textAlign: 'start' }} placeholder="1 2 3 ou N P"
                  value={f.niveaux.join(' ')}
                  onChange={(e) => poser(p.cle, p.libelle, { niveaux: e.target.value.split(/[\s,;/]+/).filter(Boolean) })} />
              </div>
              <div>
                <label style={lbl} htmlFor={`${p.cle}-alerte`}>Alerte de rupture (j)</label>
                <input id={`${p.cle}-alerte`} inputMode="decimal" style={petit} placeholder="7"
                  value={f.alerteRuptureJours === null || f.alerteRuptureJours === undefined ? '' : String(f.alerteRuptureJours)}
                  onChange={(e) => poser(p.cle, p.libelle, { alerteRuptureJours: num(e.target.value) })} />
              </div>
            </div>
          </fieldset>
        );
      })}

      <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, display: 'grid', gap: 10 }}>
        <strong style={{ fontSize: 13.5, color: 'var(--text-strong)' }}>Étiquettes des aliquots</strong>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.55 }}>
          Imprimante Zebra : taille et résolution de l’étiquette. <strong>Zebra branchée sur le poste</strong> qui
          édite les étiquettes : rien à régler ici — installez l’agent gratuit <em>Zebra Browser Print</em> sur
          cet ordinateur et acceptez le site à la première impression. <strong>Zebra du réseau</strong> : son
          adresse (IPv4 privée, ex. 192.168.1.50 — envoi direct par le MOTEUR sur le port 9100, qui doit
          être sur le même réseau qu’elle). Le format par défaut — 40 × 20 mm, 2 de front, coupée en
          deux — a été <strong>mesuré sur la Zebra du laboratoire</strong> (essais des 7 et 11/10/2026) ;
          l’étiquette porte le produit, le niveau et le lot, puis la reconstitution et la DLU.
        </p>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--amber-800)', background: 'var(--amber-50)', borderRadius: 'var(--radius-sm)', padding: '6px 10px', lineHeight: 1.5 }}>
          ⚠️ L’<strong>écart entre colonnes</strong> et le <strong>calage</strong> dépendent de VOTRE rouleau et de
          VOTRE imprimante — pas du logiciel. Ils se mesurent : imprimez la mire depuis la fenêtre
          d’étiquetage, lisez le premier repère entièrement visible, reportez-le ici. Une valeur devinée
          décale toute la colonne de droite, ou rogne le haut de chaque rangée.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div><label style={lbl} htmlFor="etq-l">Largeur (mm)</label>
            <input id="etq-l" style={petit} value={etq.largeurMm ?? ''} placeholder="38" onChange={(e) => poserEtq({ largeurMm: num(e.target.value) ?? undefined } as Partial<ConfigEtiquettesConf>)} /></div>
          <div><label style={lbl} htmlFor="etq-h">Hauteur (mm)</label>
            <input id="etq-h" style={petit} value={etq.hauteurMm ?? ''} placeholder="13" onChange={(e) => poserEtq({ hauteurMm: num(e.target.value) ?? undefined } as Partial<ConfigEtiquettesConf>)} /></div>
          <div><label style={lbl} htmlFor="etq-dpi">Résolution</label>
            <select id="etq-dpi" value={String(etq.dpi ?? '')} style={{ ...inputStyle, height: 30, fontSize: 12.5 }}
              onChange={(e) => poserEtq({ dpi: (e.target.value === '300' ? 300 : e.target.value === '203' ? 203 : undefined) } as Partial<ConfigEtiquettesConf>)}>
              <option value="">— à confirmer —</option><option value="203">203 dpi</option><option value="300">300 dpi</option>
            </select></div>
          <div><label style={lbl} htmlFor="etq-ip">Adresse de la Zebra</label>
            <input id="etq-ip" style={{ ...petit, width: 150, textAlign: 'start' }} value={etq.adresse ?? ''} placeholder="aucune (Zebra du poste)"
              onChange={(e) => poserEtq({ adresse: e.target.value.trim() })} /></div>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {/* Les réglages PROPRES AU POSTE — mesurés, jamais devinés. */}
          <div><label style={lbl} htmlFor="etq-g">Écart entre colonnes (mm)</label>
            <input id="etq-g" style={petit} placeholder="3" value={etq.gouttiereMm ?? ''}
              onChange={(e) => poserEtq({ gouttiereMm: num(e.target.value) ?? undefined } as Partial<ConfigEtiquettesConf>)} /></div>
          <div><label style={lbl} htmlFor="etq-c">Calage vertical (mm)</label>
            <input id="etq-c" style={petit} placeholder="2,6" value={etq.calageMm ?? ''}
              onChange={(e) => poserEtq({ calageMm: num(e.target.value) ?? undefined } as Partial<ConfigEtiquettesConf>)} /></div>
          <div><label style={lbl} htmlFor="etq-d">Libellés par étiquette</label>
            <select id="etq-d" value={String(etq.libellesParEtiquette ?? '')} style={{ ...inputStyle, height: 30, fontSize: 12.5 }}
              onChange={(e) => poserEtq({ libellesParEtiquette: num(e.target.value) ?? undefined } as Partial<ConfigEtiquettesConf>)}>
              <option value="">— par défaut (2) —</option>
              <option value="2">2 — tube de 1,5 mL</option>
              <option value="3">3 — tube de 0,5 mL</option>
            </select></div>
          <div><label style={lbl} htmlFor="etq-m">Module du code (points)</label>
            <input id="etq-m" style={petit} placeholder="4" value={etq.moduleDm ?? ''}
              onChange={(e) => poserEtq({ moduleDm: num(e.target.value) ?? undefined } as Partial<ConfigEtiquettesConf>)} /></div>
        </div>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          Module 4 points = 0,50 mm à 203 dpi — lu à la douchette et au téléphone le 11/10/2026.
          Sous 2 points (0,25 mm), aucun lecteur ne suit : le moteur relève la valeur.
        </span>
        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-body)' }}>Planches A4 autocollantes</div>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.5 }}>
          Repli quand la Zebra est indisponible. La planche porte <strong>exactement la même étiquette</strong>
          {' '}que la Zebra — 40 × 10 mm, même DataMatrix, mêmes trois lignes : il n’y a qu’un format à connaître.
          La feuille étant entièrement autocollante, les étiquettes sont <strong>jointives</strong> (pas = taille)
          et les traits de coupe sont imprimés. 5 × 27 = 135 étiquettes par page.
        </p>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          {([
            ['lignes', 'Lignes', '27'], ['colonnes', 'Colonnes', '5'],
            ['margeHautMm', 'Marge haut (mm)', '12'], ['margeGaucheMm', 'Marge gauche (mm)', '5'],
            ['largeurMm', 'Étiquette L (mm)', '40'], ['hauteurMm', 'Étiquette H (mm)', '10'],
            ['pasHorizontalMm', 'Pas horizontal (mm)', '40'], ['pasVerticalMm', 'Pas vertical (mm)', '10'],
          ] as [keyof GrillePlancheConf, string, string][]).map(([k, l, ph]) => (
            <div key={k}><label style={lbl} htmlFor={`pl-${k}`}>{l}</label>
              <input id={`pl-${k}`} style={petit} placeholder={ph} value={etq.planche?.[k] ?? ''}
                onChange={(e) => poserPlanche({ [k]: num(e.target.value) ?? undefined } as Partial<GrillePlancheConf>)} /></div>
          ))}
        </div>
        <div>
          <label style={lbl} htmlFor="pl-verifie">Planche lue à la douchette le</label>
          <input id="pl-verifie" type="date" style={{ ...petit, width: 150, textAlign: 'start' }} value={etq.planche?.verifieLe ?? ''}
            onChange={(e) => poserPlanche({ verifieLe: e.target.value })} />
          <span style={{ fontSize: 12, color: 'var(--text-muted)', marginInlineStart: 8 }}>
            Le DataMatrix de la planche est dessiné par l’écran et n’a jamais été relu par une machine. Imprimez une planche, scannez-la, puis saisissez le jour de l’essai : l’avertissement disparaît. Le réglage est journalisé comme les autres.
          </span>
        </div>
      </div>
    </div>
  );
}
